<script setup lang="ts">
  import { useValidation } from '@qicgroupdoha/qic-use'
  import { useValidators } from '@/shared/composables/useValidators'
  import { useVisitorsStore } from '@/shared/store'
  import { PhoneInfo, VisitorType } from '@/shared/types/store.types'
  import { PhoneInputQC } from '@qicgroupdoha/qic-connect-ds'
  import { storeToRefs } from 'pinia'
  import { computed, nextTick, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'

  interface Props {
    visitor: VisitorType
    disabled?: boolean
    tabindex?: number
    contactsSame?: boolean
    isHolder?: boolean
    isEdit?: boolean
  }

  interface Emits {
    (e: 'phone-completed', value: boolean): void
    (e: 'phone-changed', value: PhoneInfo): void
  }

  const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    tabindex: 8,
    contactsSame: false,
    isHolder: false,
    isEdit: false,
  })

  const emit = defineEmits<Emits>()

  const visitorsStore = useVisitorsStore()
  const { visitorsList } = storeToRefs(visitorsStore)
  const { t } = useI18n()
  const { withMessage } = useValidators()

  const validateQatarPhone = (phone: string) => {
    if (phoneInfoLocal.value.countryCode === 'QA') {
      const qatarRegex = /^\+974[3,5,6,7]/
      return qatarRegex.test(phone.replace(/\s/g, ''))
    }
    return true
  }

  const phoneInfoLocal = ref<PhoneInfo>({
    phone: '',
    countryCode: 'QA',
    isValid: false,
  })
  const holder = computed(() => visitorsList.value.find((v) => v.srNo === 1))

  const setPhoneInfoLocal = (phoneInfo?: PhoneInfo) => {
    phoneInfoLocal.value.phone = phoneInfo?.phone || ''
    phoneInfoLocal.value.isValid = phoneInfo?.isValid || false
    nextTick(() => {
      phoneInfoLocal.value.countryCode = phoneInfo?.countryCode || 'QA'
    })
  }
  const initLocalState = () => {
    if (props.isHolder) {
      setPhoneInfoLocal(props.visitor.phoneInfo)
    } else if (props.contactsSame && holder.value) {
      setPhoneInfoLocal(holder.value.phoneInfo)
    } else {
      setPhoneInfoLocal(props.visitor.phoneInfo)
    }
  }

  const emitPhoneChanged = () => {
    emit('phone-changed', {
      phone: phoneInfoLocal.value.phone,
      countryCode: phoneInfoLocal.value.countryCode,
      isValid: phoneInfoLocal.value.isValid,
    })
  }

  const handleCompleted = (value: boolean) => {
    phoneInfoLocal.value.isValid = value
    emit('phone-completed', value)
    if (value) {
      emitPhoneChanged()
    }
  }

  const prevPhoneInfo = ref<PhoneInfo>()
  watch(
    () => props.contactsSame,
    (newValue) => {
      if (newValue && holder.value) {
        prevPhoneInfo.value = props.visitor.phoneInfo
        setPhoneInfoLocal(holder.value.phoneInfo)
      } else if (!props.isHolder && props.isEdit) {
        setPhoneInfoLocal(prevPhoneInfo.value || props.visitor.phoneInfo)
      } else if (!props.isHolder && !props.isEdit) {
        setPhoneInfoLocal()
      }
      emitPhoneChanged()
    }
  )

  const validPhone = computed(() =>
    withMessage(t('qic_error_invalid_phone'), () => phoneInfoLocal.value.isValid || false)
  )

  const validQatarPhone = computed(() =>
    withMessage(t('qic_error_invalid_qa_phone'), () =>
      validateQatarPhone(phoneInfoLocal.value.phone)
    )
  )

  const validQatarPhoneLenght = computed(() =>
    withMessage(t('qic_error_invalid_qa_phone_lenght'), () => {
      if (phoneInfoLocal.value.countryCode !== 'QA') return true
      return phoneInfoLocal.value.phone.replace(/[\s+]/g, '').length === 11
    })
  )

  const validation = computed(() => ({
    phone: {
      required: true,
      validQatarPhoneLenght: validQatarPhoneLenght.value,
      validPhone: validPhone.value,
      validQatarPhone: validQatarPhone.value,
    },
  }))
  const { v$, errors } = useValidation(validation.value, { phone: phoneInfoLocal.value.phone })
  const phoneError = computed(() => (errors.phone?.length > 0 ? [errors.phone[0] as string] : []))

  watch(
    () => props.visitor,
    () => {
      nextTick(() => {
        initLocalState()
      })
    },
    {
      flush: 'post',
      immediate: true,
    }
  )

  defineExpose({
    setCountryCode(code: string) {
      if (code === 'UNKNOWN' || code === phoneInfoLocal.value.countryCode) return
      setPhoneInfoLocal({ phone: '', countryCode: code, isValid: false })
    },
  })
</script>

<template>
  <PhoneInputQC
    v-model="phoneInfoLocal.phone"
    v-model:country-iso.uppercase="phoneInfoLocal.countryCode"
    :errors="phoneError"
    :disabled="disabled"
    :tabindex="tabindex"
    data-test="phone-input"
    @focus="v$.phone.$reset"
    @completed="handleCompleted"
  />
</template>
